import { Button, Drawer, FormField, Input, SegmentedControl } from '@ntconsult/aluma-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCheckProfessionals } from '../hooks/useCheckProfessionals';
import type { PoolAdvancedValues, PoolFilterState } from '../hooks/usePoolFilters';
import type { CheckProfessionalOption, PoolFilterOptions } from '../types/Checks';
import { professionalLabel } from '../utils/labels';
import { MultiSearchCombobox } from './MultiSearchCombobox';
import { MultiSelectFilter } from './MultiSelectFilter';

const TITLE_ID = 'pool-advanced-filters-title';
const FORM_ID = 'pool-advanced-filters';

type YesNoChoice = 'all' | 'yes' | 'no';

type PoolFiltersDrawerProps = {
  open: boolean;
  onClose: () => void;
  filterState: PoolFilterState;
  filterOptions: PoolFilterOptions | undefined;
};

function draftFrom({ filters, professionals }: PoolFilterState): PoolAdvancedValues {
  return {
    professionals,
    profileSearch: filters.profileSearch ?? '',
    projectIds: filters.projectIds ?? [],
    billable: filters.billable ?? null,
    notified: filters.notified ?? null,
    notes: filters.notes ?? '',
    tagIds: filters.tagIds ?? [],
  };
}

const toProfessionalOption = (professional: CheckProfessionalOption) => ({
  id: professional.id,
  name: professional.name.trim(),
});

const toChoice = (value: boolean | null): YesNoChoice =>
  value === null ? 'all' : value ? 'yes' : 'no';
const fromChoice = (choice: YesNoChoice): boolean | null =>
  choice === 'all' ? null : choice === 'yes';

/**
 * No molde de `AdvancedFiltersDrawer`: rascunho aplicado de uma vez. Aqui o
 * Profissionais são seleção exata (a coluna busca por texto) e a faixa de % fica
 * só na coluna, como em Alocações.
 */
export function PoolFiltersDrawer({
  open,
  onClose,
  filterState,
  filterOptions,
}: PoolFiltersDrawerProps) {
  const { t } = useTranslation(['alocacao', 'common']);
  const [draft, setDraft] = useState(() => draftFrom(filterState));
  const [wasOpen, setWasOpen] = useState(open);

  // Reinicia o rascunho a partir do que está aplicado sempre que o painel abre.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setDraft(draftFrom(filterState));
  }

  const date = filterState.filters.date;
  // O `MultiSearchCombobox` chama este hook no próprio render; só a `date` varia.
  const useProfessionalsOnDate = useCallback(
    // biome-ignore lint/correctness/useHookAtTopLevel: ver comentário acima
    (search: string) => useCheckProfessionals('pool', search, date),
    [date],
  );

  const apply = () => {
    filterState.applyAdvanced(draft);
    onClose();
  };

  return (
    <Drawer open={open} onClose={onClose} size="md" ariaLabelledBy={TITLE_ID}>
      <Drawer.Header
        titleId={TITLE_ID}
        dismissLabel={t('common:close')}
        title={t('alocacao:filtros.avancados')}
        subtitle={t('alocacao:consultas.pool.filtrosDescricao')}
        onClose={onClose}
      />
      <Drawer.Body>
        <form
          id={FORM_ID}
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            apply();
          }}
        >
          <FormField label={t('alocacao:consultas.colunas.profissional')}>
            <MultiSearchCombobox
              label={t('alocacao:consultas.colunas.profissional')}
              placeholder={t('alocacao:filtros.profissionaisPlaceholder')}
              searchPlaceholder={t('alocacao:consultas.filtros.nomeOuMatriculaPlaceholder')}
              emptyLabel={t('alocacao:filtros.semResultados')}
              removeChipLabel={(name) => t('alocacao:filtros.chips.remover', { filter: name })}
              value={draft.professionals}
              onChange={(professionals) => setDraft((d) => ({ ...d, professionals }))}
              useSearch={useProfessionalsOnDate}
              toOption={toProfessionalOption}
              getItemLabel={professionalLabel}
            />
          </FormField>
          <FormField label={t('alocacao:consultas.colunas.perfil')}>
            <Input
              value={draft.profileSearch}
              onChange={(e) => setDraft((d) => ({ ...d, profileSearch: e.target.value }))}
            />
          </FormField>
          <FormField label={t('alocacao:tabela.projeto')}>
            <MultiSelectFilter
              options={(filterOptions?.projects ?? []).map((project) => ({
                id: project.id,
                label: `${project.axisCode} - ${project.name}`,
              }))}
              value={draft.projectIds}
              onChange={(projectIds) => setDraft((d) => ({ ...d, projectIds }))}
            />
          </FormField>
          <FormField label={t('alocacao:tabela.faturando')}>
            <SegmentedControl
              aria-label={t('alocacao:tabela.faturando')}
              value={toChoice(draft.billable)}
              onValueChange={(choice) =>
                setDraft((d) => ({ ...d, billable: fromChoice(choice as YesNoChoice) }))
              }
            >
              <SegmentedControl.Item value="all">
                {t('alocacao:consultas.filtros.todos')}
              </SegmentedControl.Item>
              <SegmentedControl.Item value="yes">{t('common:yes')}</SegmentedControl.Item>
              <SegmentedControl.Item value="no">{t('common:no')}</SegmentedControl.Item>
            </SegmentedControl>
          </FormField>
          <FormField label={t('alocacao:consultas.colunas.avisado')}>
            <SegmentedControl
              aria-label={t('alocacao:consultas.colunas.avisado')}
              value={toChoice(draft.notified)}
              onValueChange={(choice) =>
                setDraft((d) => ({ ...d, notified: fromChoice(choice as YesNoChoice) }))
              }
            >
              <SegmentedControl.Item value="all">
                {t('alocacao:consultas.filtros.todos')}
              </SegmentedControl.Item>
              <SegmentedControl.Item value="yes">{t('common:yes')}</SegmentedControl.Item>
              <SegmentedControl.Item value="no">{t('common:no')}</SegmentedControl.Item>
            </SegmentedControl>
          </FormField>
          <FormField label={t('alocacao:filtros.anotacoes')}>
            <Input
              value={draft.notes}
              onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
              placeholder={t('alocacao:filtros.anotacoesPlaceholder')}
            />
          </FormField>
          <FormField label={t('alocacao:form.tags')}>
            <MultiSelectFilter
              options={(filterOptions?.tags ?? []).map((tag) => ({ id: tag.id, label: tag.name }))}
              value={draft.tagIds}
              onChange={(tagIds) => setDraft((d) => ({ ...d, tagIds }))}
            />
          </FormField>
        </form>
      </Drawer.Body>
      <Drawer.Footer className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          {t('alocacao:filtros.cancelar')}
        </Button>
        <Button type="submit" form={FORM_ID}>
          {t('alocacao:filtros.aplicar')}
        </Button>
      </Drawer.Footer>
    </Drawer>
  );
}
